import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { Sparkles, Image, ShieldCheck } from 'lucide-react';
import GalleryView from '../components/GalleryView';

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [activeGallery, setActiveGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadGalleries() {
      const data = await fetchCollection('galleries');
      if (data && data.items) {
        setGalleries(data.items);
        // Sæt det første galleri som aktivt som standard
        if (data.items.length > 0) {
          setActiveGallery(data.items[0]);
        }
      }
      setLoading(false);
    }
    loadGalleries();
  }, []);

  const metaTitle = "Galleri - KBS Tømrer – Montage ApS";
  const metaDesc = "Se billeder af vores udførte tømrerarbejde, tag, døre, vinduer, tilbygninger, gulve og montage i Malling, Odder, Århus og omegn.";

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-22.jpg"
            alt="KBS Tømrer Galleri"
            className="h-full w-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Vores Galleri
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Se billeder af vores færdige projekter. Vi leverer altid et flot og holdbart resultat, vi kan stå inde for.
          </p>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16 bg-white flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {galleries.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              Der blev ikke fundet nogen gallerier i CMS'et.
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              {/* Gallery Navigation Tabs */}
              <div className="flex flex-wrap justify-center gap-2 border-b border-slate-100 pb-6">
                {galleries.map((gallery) => {
                  const isActive = activeGallery?.id === gallery.id;
                  return (
                    <button
                      key={gallery.id}
                      onClick={() => setActiveGallery(gallery)}
                      className={`px-5 py-3 text-sm font-bold rounded-xl transition-all cursor-pointer ${
                        isActive
                          ? 'bg-brand-primary text-white shadow-md'
                          : 'bg-slate-50 text-brand-charcoal-light hover:bg-slate-100 border border-slate-200/60'
                      }`}
                    >
                      {gallery.title.da.replace(/\s*[Gg]alleri\s*/g, '').trim()}
                    </button>
                  );
                })}
              </div>

              {/* Active Gallery View */}
              {activeGallery && (
                <div className="animate-in fade-in duration-300">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                      <h2 className="font-display text-2xl font-bold text-brand-charcoal flex items-center gap-2">
                        {activeGallery.title.da}
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">
                        Se eksempler på vores professionelle arbejde inden for {activeGallery.title.da.toLowerCase()}.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold text-brand-primary bg-brand-primary/5 border border-brand-primary/10 px-3.5 py-2 rounded-xl w-fit">
                      <ShieldCheck className="h-4 w-4" />
                      100% dansk kvalitetshåndværk
                    </div>
                  </div>

                  <GalleryView images={activeGallery.images} title={activeGallery.title.da} />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
